import { useState } from 'react';
import { Paper, Stack, Text, Group, Progress, Divider, Badge, Button, ThemeIcon, List } from '@mantine/core';
import { IconTrendingDown, IconCalendar, IconCoin, IconSettings, IconInfoCircle } from '@tabler/icons-react';
import { calculateDepreciation, formatDepreciationCurrency, formatDepreciationPercent, getDepreciationMethodName, type DepreciationMethod } from '@/lib/depreciation';
import { DepreciationSetupModal } from './DepreciationSetupModal';
import type { Asset } from '@/types/models';

interface DepreciationInfoProps {
  asset: Asset;
  onSuccess?: () => void;
}

export function DepreciationInfo({ asset, onSuccess }: DepreciationInfoProps) {
  const [setupModalOpen, setSetupModalOpen] = useState(false);
  // Check if asset is depreciable and has required fields
  const isFullyConfigured = asset.is_depreciable && asset.depreciable_cost && asset.asset_life_months && asset.date_acquired;

  if (!isFullyConfigured) {
    return (
      <Stack gap="md">
        <Paper p="lg" withBorder>
          <Stack gap="md" align="center">
            <ThemeIcon size={48} radius="xl" color="gray" variant="light">
              <IconTrendingDown size={24} />
            </ThemeIcon>
            <Text fw={600} size="lg">Depreciation Tracking</Text>
            <Text size="sm" c="dimmed" ta="center" maw={400}>
              Track the declining value of this asset over time for accounting and tax purposes.
            </Text>
          </Stack>
        </Paper>

        <Paper p="md" withBorder bg="gray.0">
          <Group gap="xs" mb="sm">
            <IconInfoCircle size={16} color="var(--mantine-color-blue-6)" />
            <Text size="sm" fw={500}>What you'll need to set up:</Text>
          </Group>
          <List size="sm" spacing="xs" c="dimmed">
            <List.Item>Depreciable cost (original purchase cost)</List.Item>
            <List.Item>Salvage value (expected value at end of life)</List.Item>
            <List.Item>Asset life in months</List.Item>
            <List.Item>Date acquired (when placed in service)</List.Item>
            <List.Item>Depreciation method (straight-line, declining balance, etc.)</List.Item>
          </List>
        </Paper>

        <Button
          leftSection={<IconSettings size={16} />}
          onClick={() => setSetupModalOpen(true)}
          fullWidth
        >
          Set Up Depreciation
        </Button>

        <DepreciationSetupModal
          opened={setupModalOpen}
          onClose={() => setSetupModalOpen(false)}
          asset={asset}
          onSuccess={onSuccess}
        />
      </Stack>
    );
  }

  const depreciationResult = calculateDepreciation({
    depreciableCost: asset.depreciable_cost,
    salvageValue: asset.salvage_value || 0,
    assetLifeMonths: asset.asset_life_months,
    dateAcquired: asset.date_acquired,
    method: (asset.depreciation_method as DepreciationMethod) || 'straight-line',
  });

  return (
    <Stack gap="md">
      <Paper p="md" withBorder>
        <Group justify="apart" mb="md">
          <Group>
            <IconTrendingDown size={20} />
            <Text fw={600}>Depreciation Summary</Text>
          </Group>
          <Badge variant="light" color="blue">
            {getDepreciationMethodName(depreciationResult.method)}
          </Badge>
        </Group>

        <Stack gap="sm">
          <Group justify="apart">
            <Text size="sm" c="dimmed">
              Original Cost
            </Text>
            <Text size="sm" fw={500}>
              {formatDepreciationCurrency(asset.depreciable_cost)}
            </Text>
          </Group>

          <Group justify="apart">
            <Text size="sm" c="dimmed">
              Salvage Value
            </Text>
            <Text size="sm" fw={500}>
              {formatDepreciationCurrency(asset.salvage_value || 0)}
            </Text>
          </Group>

          <Divider />

          <Group justify="apart">
            <Text size="sm" c="dimmed">
              Current Book Value
            </Text>
            <Text size="sm" fw={700} c="blue">
              {formatDepreciationCurrency(depreciationResult.currentBookValue)}
            </Text>
          </Group>

          <Group justify="apart">
            <Text size="sm" c="dimmed">
              Accumulated Depreciation
            </Text>
            <Text size="sm" fw={500} c="red">
              {formatDepreciationCurrency(depreciationResult.accumulatedDepreciation)}
            </Text>
          </Group>

          <Group justify="apart">
            <Text size="sm" c="dimmed">
              Monthly Depreciation
            </Text>
            <Text size="sm" fw={500}>
              {formatDepreciationCurrency(depreciationResult.monthlyDepreciation)}
            </Text>
          </Group>
        </Stack>
      </Paper>

      <Paper p="md" withBorder>
        <Stack gap="sm">
          <Group justify="apart">
            <Group>
              <IconCalendar size={16} />
              <Text size="sm" fw={500}>
                Age & Progress
              </Text>
            </Group>
            <Text size="sm" c="dimmed">
              {depreciationResult.yearsElapsed.toFixed(1)} years ({depreciationResult.monthsElapsed} months)
            </Text>
          </Group>

          <div>
            <Group justify="apart" mb={4}>
              <Text size="xs" c="dimmed">
                Depreciation Progress
              </Text>
              <Text size="xs" fw={500}>
                {formatDepreciationPercent(depreciationResult.percentDepreciated)}
              </Text>
            </Group>
            <Progress
              value={Math.min(depreciationResult.percentDepreciated, 100)}
              color={depreciationResult.percentDepreciated >= 100 ? 'red' : 'blue'}
              size="md"
            />
          </div>

          <Group justify="apart">
            <Group>
              <IconCoin size={16} />
              <Text size="sm" fw={500}>
                Remaining Value
              </Text>
            </Group>
            <Text size="sm" c="dimmed">
              {formatDepreciationCurrency(depreciationResult.remainingValue)}
            </Text>
          </Group>
        </Stack>
      </Paper>
    </Stack>
  );
}
